import { useRouter } from 'next/router'
import React, { FC, memo } from 'react'
import styled from 'styled-components'

import { Price as PriceDefinition, Product } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

type TitleAndPriceProps = Pick<Product, 'name'> & {
  buyTwoEnabled?: boolean
  price?: PriceDefinition
}

export const TitleAndPriceWrapper = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  minHeight: 50,
  padding: `0 ${theme.padding.phablet}`,
  width: '100%',

  [up('mobileStyleThreshold')]: {
    marginTop: '17px',
    minHeight: 0,
    marginRight: '17px',
    padding: 0,
  },
}))

const ProductTitle = styled.h1({
  fontSize: '.875rem',
  fontWeight: 400,
  flex: '1',
  padding: '.4em 1em .4em 0',

  [up('mobileStyleThreshold')]: {
    fontSize: '1rem',
    lineHeight: 1.2,
    padding: '3px 0 13px',
  },
})

const TwoTimesIndicator = styled.span`
  font-weight: bold;
  line-height: inherit;
  text-transform: none;
`

const PriceWrap = styled.div(({
  display: 'flex',
  whiteSpace: 'nowrap',
  fontSize: '0.875rem',
  marginLeft: '2rem',
  alignSelf: 'center',
  [up('mobileStyleThreshold')]: {
    alignSelf: 'baseline',
  },

  [up('phablet')]: {
    fontSize: '15px',
    marginBottom: 5,
  },
}))

const BeforePrice = styled.span(({
  marginRight: '.25em',
  opacity: 0.8,
  textDecoration: 'line-through',
}))

const Price = styled.span(({ theme }) => ({
  [up('mobileStyleThreshold')]: {
    fontWeight: 'bold',
  },

  'span + &': {
    color: theme.colors.red,
  },
}))

const StyledTaxMessage = styled.aside(({ theme }) => ({
  flex: '1 1 100%',
  color: theme.colors.greyLight,
  fontSize: '0.75rem',
  marginBottom: 10,
}))

const ProductTitleWrapper = styled.div`
  display: flex;
  flex: 1 1 100%;
`

export const TitleAndPrice: FC<TitleAndPriceProps> = memo(({ buyTwoEnabled, name, price }) => {
  const { t } = useTranslation()
  const { locale } = useRouter()
  const beforePrice = (price?.discount?.format ? price.format : undefined) || undefined
  const currentPrice = price?.discount?.format || price?.format || ''

  return (
    <TitleAndPriceWrapper>
      <ProductTitleWrapper>
        <ProductTitle>
          {buyTwoEnabled && (
            <TwoTimesIndicator>
              2x
              {' '}
            </TwoTimesIndicator>
          )}
          {name}
        </ProductTitle>
        {price && (
          <PriceWrap>
            {beforePrice && (
              <BeforePrice>{beforePrice}</BeforePrice>
            )}
            <Price>
              {currentPrice}
            </Price>
          </PriceWrap>
        )}
      </ProductTitleWrapper>

      {(locale === 'en-CA' || locale === 'fr-CA') && (
        <StyledTaxMessage>{t('products:DUTIES_TAXES_AND_FEES_MAY_BE_COLLECTED_ON_DELIVERY')}</StyledTaxMessage>
      )}
      {locale === 'nb-NO' && (
        <StyledTaxMessage>{t('products:INCLUDING_VALUE_ADDED_TAX')}</StyledTaxMessage>
      )}
    </TitleAndPriceWrapper>
  )
})
